import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useThemeContext } from '../contexts/ThemeContext';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import KPICard from '../components/dashboard/KPICard';


import { Select, Checkbox } from '../components/ui';
import { Loading } from '../components/utils';
import { useDashboard } from '../hooks/useDashboard';
import { useAppContext } from '../contexts/Storage';
import { useCurrency } from '../utils/globalCurrency';
import { useGetBalanceSheet, BalanceSheetApiResponse } from '../api/accounts';
import DemoBanner from '../components/dashboard/DemoBanner';
import emptyFolderIcon from '../assets/images/empty_folder.png';
import copyIcon from '../assets/images/copy.png';
import computerIcon from '../assets/images/computer.png';

const APP_URL = 'https://app.balancio.in';
const DISMISS_DESKTOP_PROMPT_KEY = 'dismiss_desktop_prompt';

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// Helper to get default date range (1 year back to today)
const getDefaultDateRange = () => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `${formatDate(oneYearAgo)} 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

// Sum all numeric values in a records object (e.g. capital, liabilities)
const sumRecordValues = (obj: Record<string, number> | undefined): number => {
  if (!obj) return 0;
  return Object.values(obj).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
};

// Sum all numeric values in a potentially nested assets object
const sumAssetValues = (assets: Record<string, any> | undefined): number => {
  if (!assets) return 0;
  let total = 0;
  Object.values(assets).forEach(val => {
    if (typeof val === 'number') {
      total += val;
    } else if (typeof val === 'object' && val !== null) {
      // Nested object like media: { cash: 1000, bank: 2000 }
      total += sumRecordValues(val as Record<string, number>);
    }
  });
  return total;
};

const DashboardPage: React.FC = () => {
  const { isDark } = useThemeContext();
  const { setNavLinks } = useNavLinksContext();
  const { user, selectedRecord: selectedRecordCtx } = useAppContext();
  const { kpis, isLoading: isKpisLoading, fetchDashboardKpis } = useDashboard();
  const { format, formatWhole } = useCurrency();
  const [selectedRecord, setSelectedRecord] = useState<string>('all');

  const defaultDates = getDefaultDateRange();

  // Balance sheet data for the selected record
  const recordIdNum = selectedRecord !== 'all' ? parseInt(selectedRecord, 10) : selectedRecordCtx?.id;
  const {
    data: balanceSheetData,
    isLoading: isBalanceSheetLoading,
  } = useGetBalanceSheet({
    filters: {
      record_id: recordIdNum,
      user_id: user?.id,
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    },
    enabled: !!recordIdNum && !!user?.id,
  });

  const balanceSheet = balanceSheetData?.data as BalanceSheetApiResponse | undefined;

  // Compute totals from balance sheet API response
  const capitalTotal = useMemo(() => {
    if (!balanceSheet?.capital) return 0;
    return sumRecordValues(balanceSheet.capital);
  }, [balanceSheet]);

  const assetsTotal = useMemo(() => {
    if (!balanceSheet?.assets) return 0;
    return sumAssetValues(balanceSheet.assets);
  }, [balanceSheet]);

  const liabilitiesTotal = useMemo(() => {
    if (!balanceSheet?.liabilities) return 0;
    return sumRecordValues(balanceSheet.liabilities);
  }, [balanceSheet]);

  // Set navigation links for the Dashboard page
  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Records' },
      ],
    });

    // Cleanup is handled by the NavLinksProvider default
  }, [setNavLinks]);

  // Fetch dashboard KPIs on component mount
  useEffect(() => {
    if (user?.id) {
      fetchDashboardKpis();
    }
  }, [fetchDashboardKpis, user?.id]);

  // Extract unique records from KPIs
  const recordOptions = useMemo(() => {
    if (!kpis || kpis.length === 0) {
      return [
        { value: 'all', label: 'All Records' },
      ];
    }

    // Get unique records from KPIs
    const uniqueRecords = new Map<number, { id: number, name: string }>();

    kpis.forEach(kpi => {
      if (kpi.record_id && kpi.record_name) {
        if (!uniqueRecords.has(kpi.record_id)) {
          uniqueRecords.set(kpi.record_id, {
            id: kpi.record_id,
            name: kpi.record_name
          });
        }
      }
    });

    // Convert to dropdown options
    const recordOptionsArray = Array.from(uniqueRecords.values())
      .map(record => ({
        value: record.id.toString(),
        label: record.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      { value: 'all', label: 'All Records' },
      ...recordOptionsArray
    ];
  }, [kpis]);

  // Filter KPIs based on selected record
  const filteredKpis = useMemo(() => {
    if (!kpis || kpis.length === 0) return [];

    if (selectedRecord === 'all') {
      return kpis;
    }

    return kpis.filter(kpi => kpi.record_id?.toString() === selectedRecord);
  }, [kpis, selectedRecord]);

  // Get KPIs for cards (top 5 or all if less than 5)
  const getKpiCards = () => {
    if (!filteredKpis || filteredKpis.length === 0) return [];

    return filteredKpis.slice(0, 5).map((kpi, index) => {
      // For each KPI, we need to format with its specific currency
      // Since format uses selected record's currency, we need a custom formatter for KPIs
      const currencySymbol = kpi.record_currency_code || '';
      const amount = kpi.balance_amount || 0;
      const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

      return {
        title: kpi.transaction_entity_name || 'Unknown',
        value: `${currencySymbol}${formattedNumber}`,
        change: kpi.difference_percentage || 0,
        isChangePositive: kpi.difference_type === 'POSITIVE',
        recordName: kpi.record_name || 'Record',
        currencyCode: kpi.record_currency_code || 'USD',
      };
    });
  };

  const kpiCards = getKpiCards();

  const handleRecordChange = (value: string) => {
    setSelectedRecord(value);
  };

  const isLoading = isKpisLoading || isBalanceSheetLoading;

  // ---- Mobile Desktop Prompt Dialog ----
  const isMobile = useIsMobile();
  const [showMobileDialog, setShowMobileDialog] = useState(() => {
    // On mobile, only show if not previously dismissed
    if (window.innerWidth < 768) {
      return !localStorage.getItem(DISMISS_DESKTOP_PROMPT_KEY);
    }
    return false;
  });

  const [dontShowAgain, setDontShowAgain] = useState(false);

  const dismissPrompt = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(DISMISS_DESKTOP_PROMPT_KEY, 'true');
    }
    setShowMobileDialog(false);
  }, [dontShowAgain]);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = APP_URL;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    dismissPrompt();
  }, [dismissPrompt]);

  const handleNoThanks = useCallback(() => {
    dismissPrompt();
  }, [dismissPrompt]);

    return (
    <div className={`h-shink-from-navbar h-fill-from-navbar lg:px-2 bg-bg_grey_light text-dark overflow-y-scroll`}>
      <div className="max-w-7xl mx-auto">
        <DemoBanner />
        {/* Mobile Desktop Prompt Dialog */}
        {isMobile && showMobileDialog && (
          <>
            {/* Backdrop overlay */}
            <div
              className="fixed inset-0 bg-gray-700 bg-opacity-50 z-40"
              onClick={handleNoThanks}
            />
            {/* Dialog */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
              <div className="bg-light rounded-2xl shadow-xl max-w-sm w-full mx-auto p-6">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-grey_light flex items-center justify-center">
                    <img src={computerIcon} alt="Desktop" className="w-8 h-8" />
                  </div>
                </div>
                {/* Title */}
                <h3 className="text-lg font-semibold text-dark text-center mb-2">
                  Desktop Experience Recommended
                </h3>
                {/* Message */}
                <p className="text-grey_dark text-sm text-center mb-6 leading-relaxed">
                  Please view on desktop for a better experience.
                  <br />
                  <span className="text-xs text-grey_dark mt-1 block">
                    Copy the link to open on your computer
                  </span>
                </p>
                {/* URL display */}
                <div className="flex items-center justify-between bg-bg_grey_light rounded-lg px-4 py-3 mb-4">
                  <span className="text-sm text-dark font-medium truncate mr-2">{APP_URL}</span>
                  <button
                    onClick={handleCopyUrl}
                    className="flex-shrink-0 flex items-center gap-1 text-primary text-sm font-medium hover:text-primary_variant transition-colors"
                  >
                    <img src={copyIcon} alt="Copy" className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                                {/* Don't show again checkbox */}
                <div className="flex justify-center mb-4">
                  <Checkbox
                    id="dont-show-again"
                    checked={dontShowAgain}
                    onChange={setDontShowAgain}
                    label="Don't show again"
                  />
                </div>
                {/* No thanks button */}
                <button
                  onClick={handleNoThanks}
                  className="w-full py-2.5 rounded-lg border border-grey_light text-grey_dark text-sm font-medium hover:bg-bg_grey_light transition-colors"
                >
                  No, thanks
                </button>
              </div>
            </div>
          </>
        )}

        {/* Header and title */}
        <div className="p-6 pb-3 bg-light rounded-t-lg mb-4 flex justify-between items-center">
          <h3 className="text-xl mx-4">Overview</h3>
          {/* Dropdown to select record */}
          <div className="relative">
            <Select
              options={recordOptions}
              value={selectedRecord}
              onChange={handleRecordChange}
              placeholder="Select record"
              className="w-48"
              disabled={isKpisLoading || kpis.length === 0}
            />
          </div>
        </div>

        {isLoading ? (
          <Loading width="30px" height="30px" className="mx-2 mb-4" />
        ) : (
          <>
            {/* Balances */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-4 text-center">
              {/* Overview Cards */}
              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-secondary`}>
                <h3 className="text-md mb-2">Capital</h3>
                <hr className="my-2 border-secondary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                  title={formatWhole(capitalTotal)}
                >
                  {formatWhole(capitalTotal)}
                </p>
              </div>

              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-primary`}>
                <h3 className="text-md mb-2">Assets</h3>
                <hr className="my-2 border-primary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                  title={formatWhole(assetsTotal)}
                >
                  {formatWhole(assetsTotal)}
                </p>
              </div>

              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-secondary`}>
                <h3 className="text-md mb-2">Liabilities</h3>
                <hr className="my-2 border-secondary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                  title={formatWhole(liabilitiesTotal)}
                >
                  {formatWhole(liabilitiesTotal)}
                </p>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4 lg:me-1">
              {kpiCards.length > 0 ? (
                kpiCards.map((kpi, index) => (
                  <KPICard
                    key={index}
                    props={{
                      title: kpi.title,
                      value: kpi.value,
                      change: kpi.change,
                      isChangePositive: kpi.isChangePositive,
                      recordName: kpi.recordName
                    }}
                  />
                ))
              ) : (
                <div className="col-span-2 text-center py-8">
                  <img
                    src={emptyFolderIcon}
                    alt="No data"
                    className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 opacity-50"
                  />
                  <p className="text-grey_dark">
                    {selectedRecord === 'all'
                      ? 'No KPI data available for any records'
                      : 'No KPI data available for the selected record'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;